import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Wrench, AlertTriangle, Loader2, Plus, Eye, Clock, CheckCircle2, ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "Chair not turning ON", "Chair not reclining", "Massage function not working",
  "Remote/control problem", "Display problem", "Airbag problem",
  "Foot/calf massage problem", "Heat function problem", "Noise/vibration",
  "Electrical/power issue", "Payment/QR issue", "Timer issue", "Chair stuck",
  "Physical damage", "Cleaning issue", "Other",
];
const PRIORITIES = ["LOW", "MEDIUM", "HIGH", "URGENT"];
const PRIO_COLOR = { LOW: "bg-secondary text-muted-foreground", MEDIUM: "bg-blue-500/15 text-blue-400", HIGH: "bg-amber-500/15 text-amber-400", URGENT: "bg-destructive/15 text-destructive" };
const STATUS_META = {
  OPEN: ["Open", "bg-blue-500/15 text-blue-400"], ACKNOWLEDGED: ["Acknowledged", "bg-indigo-500/15 text-indigo-300"],
  IN_PROGRESS: ["In Progress", "bg-amber-500/15 text-amber-400"], WAITING_FOR_PARTS: ["Waiting for Parts", "bg-orange-500/15 text-orange-400"],
  WAITING_FOR_TECHNICIAN: ["Waiting for Technician", "bg-orange-500/15 text-orange-400"], RESOLVED: ["Resolved", "bg-emerald-500/15 text-emerald-400"],
  CLOSED: ["Closed", "bg-secondary text-muted-foreground"], CANCELLED: ["Cancelled", "bg-secondary text-muted-foreground"],
};
const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const dt = (s) => s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

function Prio({ p }) { return <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${PRIO_COLOR[p]}`}>{p}</span>; }
function Stat({ p }) { const [t, c] = STATUS_META[p] || [p, "bg-secondary"]; return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${c}`}>{t}</span>; }

function AuthMedia({ att }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u; api.get(`/maintenance/attachments/${att.id}`, { responseType: "blob" })
      .then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [att.id]);
  if (!url) return <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-secondary"><ImageIcon size={20} className="text-muted-foreground" /></div>;
  if (att.kind === "video") return <video src={url} controls className="h-24 rounded-lg" />;
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={att.filename} className="h-24 w-24 rounded-lg object-cover" /></a>;
}

const fileToAttachment = (file) => new Promise((resolve) => {
  const r = new FileReader();
  r.onload = () => resolve({ filename: file.name, content_type: file.type || "application/octet-stream", data: r.result });
  r.readAsDataURL(file);
});

function ReportForm({ presetLocation, presetChair, onDone }) {
  const [locations, setLocations] = useState([]);
  const [chairs, setChairs] = useState([]);
  const [locationId, setLocationId] = useState(presetLocation || "");
  const [chairId, setChairId] = useState(presetChair || "");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settlement/locations").then((r) => setLocations(r.data)).catch(() => api.get("/admin/locations").then((r) => setLocations(r.data)).catch(() => {})); }, []);
  useEffect(() => {
    if (!locationId) { setChairs([]); return; }
    api.get("/admin/chairs").then((r) => setChairs(r.data.filter((c) => c.location_id === locationId))).catch(() => {});
  }, [locationId]);

  const submit = async () => {
    if (!locationId || !chairId) { toast.error("Select location and chair"); return; }
    if (!category) { toast.error("Select a problem category"); return; }
    if (category === "Other" && !description.trim()) { toast.error("Please describe the problem"); return; }
    setBusy(true);
    try {
      const attachments = await Promise.all(files.map(fileToAttachment));
      const { data } = await api.post("/maintenance/tickets", { location_id: locationId, chair_id: chairId, category, priority, description, attachments });
      toast.success(`Ticket ${data.ticket_id} created`);
      onDone && onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div><Label>Location</Label>
        <Select value={locationId} onValueChange={(v) => { setLocationId(v); setChairId(""); }}>
          <SelectTrigger className="mt-1.5" data-testid="mnt-location-select"><SelectValue placeholder="Select location" /></SelectTrigger>
          <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Massage Chair</Label>
        <Select value={chairId} onValueChange={setChairId} disabled={!locationId}>
          <SelectTrigger className="mt-1.5" data-testid="mnt-chair-select"><SelectValue placeholder={locationId ? "Select chair" : "Select location first"} /></SelectTrigger>
          <SelectContent>{chairs.map((c) => <SelectItem key={c.id} value={c.id}>{c.slug} — {c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Problem Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="mt-1.5" data-testid="mnt-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Priority</Label>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger className="mt-1.5" data-testid="mnt-priority-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Describe the problem</Label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
          placeholder="Example: MUM1-01 is switching ON but the foot rollers are not working."
          className="mt-1.5 w-full rounded-lg border border-border bg-background p-3 text-sm" data-testid="mnt-description-input" />
      </div>
      <div><Label>Photos / Videos</Label>
        <Input type="file" accept="image/*,video/*" multiple onChange={(e) => setFiles(Array.from(e.target.files))} className="mt-1.5" data-testid="mnt-file-input" />
        {files.length > 0 && <div className="mt-1 text-xs text-muted-foreground">{files.length} file(s) selected</div>}
      </div>
      <Button onClick={submit} disabled={busy} className="w-full bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="mnt-submit-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Report"}
      </Button>
    </div>
  );
}

/* ---------------- STAFF panel ---------------- */
export function StaffMaintenance() {
  const [tickets, setTickets] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/maintenance/tickets").then((r) => setTickets(r.data)).catch(() => setTickets([])), []);
  useEffect(() => { load(); }, [load]);

  const confirm = async (t, fixed) => {
    try { await api.post(`/maintenance/tickets/${t.id}/confirm`, { fixed }); toast.success(fixed ? "Marked as fixed" : "Reopened — admin notified"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openList = (tickets || []).filter((t) => !["RESOLVED", "CLOSED", "CANCELLED"].includes(t.status));
  const doneList = (tickets || []).filter((t) => ["RESOLVED", "CLOSED"].includes(t.status));

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-serif text-lg font-semibold">Report Chair Problem</h2>
        <Button onClick={() => setOpen(true)} className="gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="staff-report-btn"><Plus size={16} /> Report Maintenance</Button>
      </div>
      {tickets === null ? <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin gold-text" /> : (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl bg-card p-4 gold-border">
            <div className="mb-2 text-sm font-semibold text-amber-400">My Open Complaints ({openList.length})</div>
            {openList.length === 0 ? <p className="py-4 text-center text-xs text-muted-foreground">None open</p> : openList.map((t) => (
              <div key={t.id} className="mb-2 rounded-lg bg-secondary/40 p-3 text-sm" data-testid={`staff-open-${t.ticket_id}`}>
                <div className="flex justify-between"><span className="font-mono text-xs">{t.ticket_id}</span><Stat p={t.status} /></div>
                <div className="mt-1">{t.chair_code} · {t.category}</div>
              </div>
            ))}
          </div>
          <div className="rounded-2xl bg-card p-4 gold-border">
            <div className="mb-2 text-sm font-semibold text-emerald-400">Resolved ({doneList.length})</div>
            {doneList.length === 0 ? <p className="py-4 text-center text-xs text-muted-foreground">None yet</p> : doneList.map((t) => (
              <div key={t.id} className="mb-2 rounded-lg bg-secondary/40 p-3 text-sm" data-testid={`staff-done-${t.ticket_id}`}>
                <div className="flex justify-between"><span className="font-mono text-xs">{t.ticket_id}</span><Stat p={t.status} /></div>
                <div className="mt-1">{t.chair_code} · {t.category}</div>
                {t.status === "RESOLVED" && t.staff_confirmation !== "fixed" && (
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => confirm(t, true)} data-testid={`confirm-fixed-${t.ticket_id}`}>Problem Fixed</Button>
                    <Button size="sm" variant="outline" className="border-destructive/50 text-destructive" onClick={() => confirm(t, false)} data-testid={`confirm-broken-${t.ticket_id}`}>Still Having Problem</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Report Maintenance Issue</DialogTitle></DialogHeader>
          <ReportForm onDone={() => { setOpen(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- ADMIN manager ---------------- */
export function MaintenanceManager() {
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [f, setF] = useState({ status: "all", priority: "all", location_id: "all", q: "" });
  const [locations, setLocations] = useState([]);

  const loadStats = useCallback(() => api.get("/maintenance/stats").then((r) => setStats(r.data)).catch(() => {}), []);
  const loadTickets = useCallback(() => {
    const p = {};
    if (f.status !== "all") p.status = f.status;
    if (f.priority !== "all") p.priority = f.priority;
    if (f.location_id !== "all") p.location_id = f.location_id;
    if (f.q) p.q = f.q;
    return api.get("/maintenance/tickets", { params: p }).then((r) => setTickets(r.data)).catch(() => setTickets([]));
  }, [f]);

  useEffect(() => { api.get("/settlement/locations").then((r) => setLocations(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadTickets(); }, [loadTickets]);
  const refresh = () => { loadStats(); loadTickets(); if (detail) api.get(`/maintenance/tickets/${detail.id}`).then((r) => setDetail(r.data)); };

  if (!stats || tickets === null) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin gold-text" /></div>;

  const cards = [
    ["Open", stats.open, "text-blue-400"], ["Urgent", stats.urgent, "text-destructive"],
    ["In Progress", stats.in_progress, "text-amber-400"], ["Waiting Technician", stats.waiting_for_technician, "text-orange-400"],
    ["Resolved", stats.resolved, "text-emerald-400"], ["Total", stats.total, "gold-text"],
  ];

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div><div className="overline-label">Service & Repairs</div><h1 className="mt-1 font-serif text-2xl font-bold">Maintenance</h1></div>
        <Button onClick={() => setReportOpen(true)} className="gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="admin-report-btn"><Plus size={16} /> New Ticket</Button>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-6">
        {cards.map(([l, v, c]) => (
          <div key={l} className="rounded-2xl bg-card p-4 border border-border" data-testid={`mnt-stat-${l.toLowerCase().replace(/ /g, "-")}`}>
            <div className="text-xs text-muted-foreground">{l}</div>
            <div className={`mt-1 text-2xl font-bold ${c}`}>{v}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 text-xs text-muted-foreground">Total maintenance cost: <span className="gold-text font-semibold">{fmt(stats.total_maintenance_cost)}</span> · Avg resolution: {stats.avg_resolution_hours}h · <span className="text-muted-foreground">tracked separately from revenue</span></div>

      {/* Filters */}
      <div className="mt-5 flex flex-wrap gap-2">
        <Input placeholder="Search ticket, chair, problem…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} className="w-56" data-testid="mnt-search" />
        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger className="w-40" data-testid="mnt-filter-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem>{Object.keys(STATUS_META).map((s) => <SelectItem key={s} value={s}>{STATUS_META[s][0]}</SelectItem>)}</SelectContent></Select>
        <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}><SelectTrigger className="w-36" data-testid="mnt-filter-priority"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Priority</SelectItem>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        <Select value={f.location_id} onValueChange={(v) => setF({ ...f, location_id: v })}><SelectTrigger className="w-48" data-testid="mnt-filter-location"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Locations</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl bg-card gold-border">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th className="p-3">Ticket</th><th>Date</th><th>Location / Chair</th><th>Problem</th><th>Priority</th><th>Reported By</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {tickets.length === 0 ? <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">No tickets found.</td></tr>
              : tickets.map((t) => (
                <tr key={t.id} className="border-b border-border/40" data-testid={`mnt-row-${t.ticket_id}`}>
                  <td className="p-3 font-mono text-xs">{t.ticket_id}</td>
                  <td className="text-xs">{dt(t.reported_at)}</td>
                  <td className="text-xs">{t.location_name}<div className="font-mono">{t.chair_code}</div></td>
                  <td className="text-xs">{t.category}</td>
                  <td><Prio p={t.priority} /></td>
                  <td className="text-xs">{t.reported_by}</td>
                  <td><Stat p={t.status} /></td>
                  <td><Button size="sm" variant="outline" className="gap-1 gold-border text-gold" onClick={() => setDetail(t)} data-testid={`mnt-view-${t.ticket_id}`}><Eye size={13} /> View</Button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Breakdown */}
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <Breakdown title="Issues by Chair" rows={stats.by_chair} />
        <Breakdown title="Issues by Location" rows={stats.by_location} />
        <Breakdown title="Issues by Category" rows={stats.by_category} />
      </div>

      {detail && <TicketDetail t={detail} onClose={() => setDetail(null)} onChange={refresh} />}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Maintenance Ticket</DialogTitle></DialogHeader>
          <ReportForm onDone={() => { setReportOpen(false); refresh(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Breakdown({ title, rows }) {
  return (
    <div className="rounded-2xl bg-card p-4 gold-border">
      <div className="mb-2 text-sm font-semibold">{title}</div>
      {(!rows || rows.length === 0) ? <p className="py-3 text-center text-xs text-muted-foreground">No data</p> : rows.slice(0, 8).map((r) => (
        <div key={r.name} className="flex justify-between border-b border-border/30 py-1.5 text-sm"><span>{r.name}</span><span className="gold-text font-semibold">{r.count}</span></div>
      ))}
    </div>
  );
}

function TicketDetail({ t, onClose, onChange }) {
  const [note, setNote] = useState("");
  const [resolveOpen, setResolveOpen] = useState(false);
  const [assign, setAssign] = useState({ assigned_to: t.assigned_to || "", technician_name: t.technician_name || "", technician_contact: t.technician_contact || "", expected_visit_date: t.expected_visit_date || "" });
  const [res, setRes] = useState({ resolution_description: "", technician_name: t.technician_name || "", parts_cost: 0, technician_cost: 0, other_cost: 0, warranty_reference: "" });

  const setStatus = async (status, extraNote) => {
    try { await api.patch(`/maintenance/tickets/${t.id}/status`, { status, note: extraNote || "" }); toast.success("Status updated"); onChange(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addNote = async () => { if (!note.trim()) return; try { await api.post(`/maintenance/tickets/${t.id}/notes`, { note }); setNote(""); toast.success("Note added"); onChange(); } catch (e) { toast.error(apiError(e)); } };
  const doAssign = async () => { try { await api.patch(`/maintenance/tickets/${t.id}/assign`, assign); toast.success("Assigned"); onChange(); } catch (e) { toast.error(apiError(e)); } };
  const doResolve = async () => { if (!res.resolution_description.trim()) { toast.error("Resolution description required"); return; } try { await api.patch(`/maintenance/tickets/${t.id}/resolve`, res); toast.success("Marked resolved"); setResolveOpen(false); onChange(); } catch (e) { toast.error(apiError(e)); } };
  const setChair = async (ms) => { try { await api.patch(`/maintenance/chair/${t.chair_id}/status`, { maintenance_status: ms }); toast.success(`Chair set: ${ms.replace(/_/g, " ")}`); onChange(); } catch (e) { toast.error(apiError(e)); } };
  const closeTicket = async () => { const n = t.resolution_description ? "" : window.prompt("Resolution note (required to close):") || ""; if (!t.resolution_description && !n.trim()) { toast.error("Resolution note required to close"); return; } setStatus("CLOSED", n); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Wrench size={18} /> {t.ticket_id} <Stat p={t.status} /></DialogTitle></DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2 text-sm">
            <Row k="Location" v={t.location_name} /><Row k="Chair" v={t.chair_code} /><Row k="Problem" v={t.category} />
            <Row k="Priority" v={<Prio p={t.priority} />} /><Row k="Reported By" v={t.reported_by} /><Row k="Reported" v={dt(t.reported_at)} />
            {t.assigned_to && <Row k="Assigned To" v={t.assigned_to} />}
            {t.technician_name && <Row k="Technician" v={`${t.technician_name} ${t.technician_contact || ""}`} />}
            {t.expected_visit_date && <Row k="Expected Visit" v={t.expected_visit_date} />}
            {t.total_cost > 0 && <Row k="Maintenance Cost" v={fmt(t.total_cost)} />}
            {t.staff_confirmation && <Row k="Staff Confirmation" v={t.staff_confirmation === "fixed" ? "Fixed ✓" : "Still broken"} />}
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Description</div>
            <div className="mt-1 rounded-lg bg-secondary/40 p-3 text-sm">{t.description || "—"}</div>
            {t.resolution_description && <><div className="mt-3 text-xs text-muted-foreground">Resolution</div><div className="mt-1 rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-300">{t.resolution_description}</div></>}
            {t.attachments?.length > 0 && <><div className="mt-3 text-xs text-muted-foreground">Attachments</div><div className="mt-1 flex flex-wrap gap-2">{t.attachments.map((a) => <AuthMedia key={a.id} att={a} />)}</div></>}
          </div>
        </div>

        {/* Chair maintenance status */}
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-secondary/40 p-3">
          <span className="text-xs text-muted-foreground">Chair status:</span>
          {[["operational", "Operational"], ["under_maintenance", "Under Maintenance"], ["out_of_service", "Out of Service"]].map(([v, l]) => (
            <Button key={v} size="sm" variant="outline" className="gold-border text-gold" onClick={() => setChair(v)} data-testid={`chair-status-${v}`}>{l}</Button>
          ))}
        </div>

        {/* Actions */}
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="gold-border text-gold" onClick={() => setStatus("ACKNOWLEDGED")} data-testid="act-ack">Acknowledge</Button>
          <Button size="sm" variant="outline" className="gold-border text-gold" onClick={() => setStatus("IN_PROGRESS")} data-testid="act-start">Start Work</Button>
          <Button size="sm" variant="outline" className="gold-border text-gold" onClick={() => setStatus("WAITING_FOR_PARTS")} data-testid="act-parts">Waiting for Parts</Button>
          <Button size="sm" variant="outline" className="gold-border text-gold" onClick={() => setStatus("WAITING_FOR_TECHNICIAN")} data-testid="act-tech">Waiting Technician</Button>
          <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => setResolveOpen(true)} data-testid="act-resolve">Mark Resolved</Button>
          <Button size="sm" variant="outline" onClick={closeTicket} data-testid="act-close">Close Ticket</Button>
          <Button size="sm" variant="outline" className="border-destructive/50 text-destructive" onClick={() => setStatus("CANCELLED")} data-testid="act-cancel">Cancel</Button>
        </div>

        {/* Assign */}
        <div className="mt-3 rounded-lg border border-border p-3">
          <div className="mb-2 text-sm font-semibold">Assign Technician</div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Assigned to" value={assign.assigned_to} onChange={(e) => setAssign({ ...assign, assigned_to: e.target.value })} data-testid="assign-to" />
            <Input placeholder="Technician name" value={assign.technician_name} onChange={(e) => setAssign({ ...assign, technician_name: e.target.value })} data-testid="assign-tech-name" />
            <Input placeholder="Technician contact" value={assign.technician_contact} onChange={(e) => setAssign({ ...assign, technician_contact: e.target.value })} data-testid="assign-tech-contact" />
            <Input type="date" value={assign.expected_visit_date || ""} onChange={(e) => setAssign({ ...assign, expected_visit_date: e.target.value })} data-testid="assign-visit" />
          </div>
          <Button size="sm" className="mt-2 bg-gold text-primary-foreground hover:bg-gold-hover" onClick={doAssign} data-testid="assign-save">Save Assignment</Button>
        </div>

        {/* Internal notes */}
        <div className="mt-3 rounded-lg border border-border p-3">
          <div className="mb-2 text-sm font-semibold">Internal Notes</div>
          {(t.internal_notes || []).map((n) => <div key={n.id} className="mb-1 rounded bg-secondary/40 p-2 text-xs"><b>{n.by}</b> · {dt(n.at)}<div>{n.note}</div></div>)}
          <div className="mt-2 flex gap-2">
            <Input placeholder="Add a note (e.g. Called technician)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" />
            <Button size="sm" className="bg-gold text-primary-foreground hover:bg-gold-hover" onClick={addNote} data-testid="note-add">Add</Button>
          </div>
        </div>

        <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
          <DialogContent className="bg-card max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Resolve Ticket</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <div><Label>Resolution Description</Label><textarea rows={2} value={res.resolution_description} onChange={(e) => setRes({ ...res, resolution_description: e.target.value })} className="mt-1.5 w-full rounded-lg border border-border bg-background p-2 text-sm" placeholder="Foot roller motor replaced and tested." data-testid="res-desc" /></div>
              <Input placeholder="Technician name" value={res.technician_name} onChange={(e) => setRes({ ...res, technician_name: e.target.value })} data-testid="res-tech" />
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">Parts ₹</Label><Input type="number" value={res.parts_cost} onChange={(e) => setRes({ ...res, parts_cost: e.target.value })} data-testid="res-parts" /></div>
                <div><Label className="text-xs">Technician ₹</Label><Input type="number" value={res.technician_cost} onChange={(e) => setRes({ ...res, technician_cost: e.target.value })} data-testid="res-techcost" /></div>
                <div><Label className="text-xs">Other ₹</Label><Input type="number" value={res.other_cost} onChange={(e) => setRes({ ...res, other_cost: e.target.value })} data-testid="res-other" /></div>
              </div>
              <div className="text-right text-sm">Total: <b className="gold-text">{fmt(Number(res.parts_cost || 0) + Number(res.technician_cost || 0) + Number(res.other_cost || 0))}</b></div>
              <Input placeholder="Warranty / service reference" value={res.warranty_reference} onChange={(e) => setRes({ ...res, warranty_reference: e.target.value })} data-testid="res-warranty" />
            </div>
            <DialogFooter><Button onClick={doResolve} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="res-confirm">Confirm Resolution</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
function Row({ k, v }) { return <div className="flex justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className="text-right font-medium">{v}</span></div>; }

export function MaintenanceOverviewCard({ onView }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/maintenance/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  return (
    <div className="rounded-2xl bg-card p-6 gold-border" data-testid="overview-maintenance-card">
      <div className="flex items-center gap-2"><Wrench className="gold-text" size={18} /><span className="overline-label">Maintenance</span>{s.urgent > 0 && <span className="ml-auto flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-xs font-bold text-destructive"><AlertTriangle size={12} /> {s.urgent} urgent</span>}</div>
      <div className="mt-4 grid grid-cols-3 gap-3 text-center">
        <div><div className="text-xs text-muted-foreground">Open</div><div className="mt-1 text-lg font-bold text-blue-400">{s.open}</div></div>
        <div><div className="text-xs text-muted-foreground">In Progress</div><div className="mt-1 text-lg font-bold text-amber-400">{s.in_progress}</div></div>
        <div><div className="text-xs text-muted-foreground">Unresolved</div><div className="mt-1 text-lg font-bold gold-text">{s.unresolved}</div></div>
      </div>
      <Button variant="outline" className="mt-4 w-full gap-1.5 gold-border text-gold" onClick={onView} data-testid="overview-view-maintenance"><Wrench size={14} /> View Maintenance</Button>
    </div>
  );
}
